import React, { useState, useEffect } from 'react';
import { Plus, FileText, Edit2, Trash2, X, Check, Filter } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import api from '../utils/api';
import { formatDate } from '../utils/format';

const emptyTarefa = { titulo: '', descricao: '', prazo: '', status: 'pendente', prioridade: 'média' };
const emptyNota = { titulo: '', conteudo: '' };

const STATUS_OPTS = ['pendente', 'em andamento', 'concluída', 'atrasada'];
const PRIORIDADE_OPTS = ['alta', 'média', 'baixa'];

const RESUMO_CARDS = [
  { label: 'Atrasadas', status: 'atrasada', accent: '#d72d21', bg: '#FEE2E2', textColor: '#d72d21' },
  { label: 'Pendentes', status: 'pendente', accent: '#f9c900', bg: '#FFFBEB', textColor: '#c99a1a' },
  { label: 'Em Andamento', status: 'em andamento', accent: '#0147a0', bg: '#EEF2FF', textColor: '#0147a0' },
  { label: 'Concluídas', status: 'concluída', accent: '#339933', bg: '#DCFCE7', textColor: '#339933' },
];

export default function TarefasNotas() {
  const [tarefas, setTarefas] = useState([]);
  const [notas, setNotas] = useState([]);
  const [tarefaForm, setTarefaForm] = useState(emptyTarefa);
  const [notaForm, setNotaForm] = useState(emptyNota);
  const [editTarefa, setEditTarefa] = useState(null);
  const [editNota, setEditNota] = useState(null);
  const [showTarefaModal, setShowTarefaModal] = useState(false);
  const [showNotaModal, setShowNotaModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPrioridade, setFilterPrioridade] = useState('');
  const [loadingTarefas, setLoadingTarefas] = useState(true);
  const [loadingNotas, setLoadingNotas] = useState(true);

  useEffect(() => { fetchTarefas(); fetchNotas(); }, []);

  const fetchTarefas = async () => {
    setLoadingTarefas(true);
    try { const r = await api.get('/tarefas'); setTarefas(r.data); }
    catch (err) { console.error(err); }
    finally { setLoadingTarefas(false); }
  };

  const fetchNotas = async () => {
    setLoadingNotas(true);
    try { const r = await api.get('/notas'); setNotas(r.data); }
    catch (err) { console.error(err); }
    finally { setLoadingNotas(false); }
  };

  const saveTarefa = async () => {
    if (!tarefaForm.titulo) return;
    try {
      if (editTarefa) await api.put(`/tarefas/${editTarefa.id}`, tarefaForm);
      else await api.post('/tarefas', tarefaForm);
      fetchTarefas();
      closeTarefaModal();
    } catch (err) { console.error(err); }
  };

  const deleteTarefa = async (id) => {
    try { await api.delete(`/tarefas/${id}`); setTarefas(p => p.filter(t => t.id !== id)); }
    catch (err) { console.error(err); }
  };

  const saveNota = async () => {
    if (!notaForm.titulo || !notaForm.conteudo) return;
    try {
      if (editNota) await api.put(`/notas/${editNota.id}`, notaForm);
      else await api.post('/notas', notaForm);
      fetchNotas();
      closeNotaModal();
    } catch (err) { console.error(err); }
  };

  const deleteNota = async (id) => {
    try { await api.delete(`/notas/${id}`); setNotas(p => p.filter(n => n.id !== id)); }
    catch (err) { console.error(err); }
  };

  const openEditTarefa = (t) => {
    setEditTarefa(t);
    setTarefaForm({ titulo: t.titulo, descricao: t.descricao || '', prazo: t.prazo || '', status: t.status, prioridade: t.prioridade || 'média' });
    setShowTarefaModal(true);
  };

  const openEditNota = (n) => {
    setEditNota(n);
    setNotaForm({ titulo: n.titulo, conteudo: n.conteudo });
    setShowNotaModal(true);
  };

  const closeTarefaModal = () => { setShowTarefaModal(false); setEditTarefa(null); setTarefaForm(emptyTarefa); };
  const closeNotaModal = () => { setShowNotaModal(false); setEditNota(null); setNotaForm(emptyNota); };

  // Filtered tasks
  const filteredTarefas = tarefas.filter(t => {
    if (filterStatus && t.status !== filterStatus) return false;
    if (filterPrioridade && (t.prioridade || 'média') !== filterPrioridade) return false;
    return true;
  });

  // Summary counts
  const counts = RESUMO_CARDS.map(c => ({
    ...c,
    count: tarefas.filter(t => t.status === c.status).length,
  }));

  return (
    <div className="space-y-6 page-enter" data-testid="tarefas-notas-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Tarefas & Notas
        </h1>
        <p className="text-slate-500 text-sm mt-1">Organize suas atividades e anotações financeiras</p>
      </div>

      {/* Top Row: Notas + Resumo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* NOTAS INTERNAS */}
        <div className="fin-card" data-testid="notas-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <FileText size={18} style={{ color: '#0147a0' }} />
              <h2 className="font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Notas Internas</h2>
              <span className="text-xs text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">{notas.length}</span>
            </div>
            <button
              onClick={() => { setEditNota(null); setNotaForm(emptyNota); setShowNotaModal(true); }}
              className="fin-btn-primary text-xs px-3 py-2"
              data-testid="nova-nota-btn"
            >
              <Plus size={14} />
              Nova Nota
            </button>
          </div>

          <div className="overflow-y-auto" style={{ minHeight: 180, maxHeight: 280 }}>
            {loadingNotas ? (
              <div className="p-5 space-y-3">
                {Array(3).fill(0).map((_, i) => (
                  <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : notas.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                <FileText size={28} className="mb-2 opacity-40" />
                <p className="text-sm">Nenhuma nota cadastrada</p>
              </div>
            ) : (
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {notas.map(n => (
                  <div
                    key={n.id}
                    className="bg-[#F0F4FF]/60 rounded-xl p-3 border border-slate-100 hover:border-[#0147a0]/30 transition-colors group"
                    data-testid={`nota-item-${n.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-800 truncate flex-1">{n.titulo}</p>
                      <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <button onClick={() => openEditNota(n)} className="p-1 rounded hover:bg-blue-50 text-slate-400 hover:text-[#0147a0]" data-testid={`edit-nota-${n.id}`}>
                          <Edit2 size={12} />
                        </button>
                        <button onClick={() => deleteNota(n.id)} className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500" data-testid={`delete-nota-${n.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{n.conteudo}</p>
                    <p className="text-xs text-slate-400 mt-1.5">{formatDate(n.dataAtualizacao?.split('T')[0] || n.dataCriacao?.split('T')[0])}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RESUMO DE TAREFAS */}
        <div className="fin-card" data-testid="resumo-tarefas">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Resumo de Tarefas</h2>
          </div>
          <div className="p-5 grid grid-cols-2 gap-4">
            {counts.map(c => (
              <button
                key={c.status}
                onClick={() => setFilterStatus(filterStatus === c.status ? '' : c.status)}
                data-testid={`resumo-${c.status.replace(' ', '-')}`}
                className="rounded-xl p-4 text-left transition-all hover:scale-[1.02] border-2"
                style={{
                  background: c.bg,
                  borderColor: filterStatus === c.status ? c.accent : 'transparent',
                }}
              >
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{c.label}</p>
                <p className="text-3xl font-bold" style={{ color: c.accent, fontFamily: 'Manrope, sans-serif' }}>
                  {c.count}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* TAREFAS TABLE */}
      <div className="fin-card" data-testid="tarefas-card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between px-5 py-4 border-b border-slate-100 gap-3">
          <div className="flex items-center gap-2">
            <h2 className="font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Tarefas</h2>
            <span className="text-xs text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">{filteredTarefas.length}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Filter: Status */}
            <div className="relative">
              <Filter size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className="fin-input pl-7 pr-3 py-1.5 text-xs w-40"
                data-testid="filter-tarefa-status"
              >
                <option value="">Filtrar por Status</option>
                {STATUS_OPTS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
            {/* Filter: Prioridade */}
            <div className="relative">
              <Filter size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <select
                value={filterPrioridade}
                onChange={e => setFilterPrioridade(e.target.value)}
                className="fin-input pl-7 pr-3 py-1.5 text-xs w-44"
                data-testid="filter-tarefa-prioridade"
              >
                <option value="">Filtrar por Prioridade</option>
                {PRIORIDADE_OPTS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
              </select>
            </div>
            {(filterStatus || filterPrioridade) && (
              <button
                onClick={() => { setFilterStatus(''); setFilterPrioridade(''); }}
                className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"
                title="Limpar filtros"
              >
                <X size={14} />
              </button>
            )}
            <button
              onClick={() => { setEditTarefa(null); setTarefaForm(emptyTarefa); setShowTarefaModal(true); }}
              className="fin-btn-primary text-xs px-3 py-2"
              data-testid="nova-tarefa-btn"
            >
              <Plus size={14} />
              Nova Tarefa
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F0F4FF] border-b border-slate-100">
                {['Título', 'Vencimento', 'Status', 'Prioridade', 'Ações'].map(h => (
                  <th key={h} className={`text-left px-3 sm:px-5 py-3 text-xs font-bold uppercase tracking-widest text-slate-400 ${h === 'Prioridade' ? 'hidden sm:table-cell' : ''}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loadingTarefas ? (
                Array(4).fill(0).map((_, i) => (
                  <tr key={i}>
                    {Array(5).fill(0).map((_, j) => (
                      <td key={j} className="px-3 sm:px-5 py-3"><div className="h-3 bg-slate-100 rounded animate-pulse" /></td>
                    ))}
                  </tr>
                ))
              ) : filteredTarefas.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 sm:px-5 py-10 text-center text-slate-400 text-sm">
                    Nenhuma tarefa encontrada
                  </td>
                </tr>
              ) : (
                filteredTarefas.map(t => (
                  <tr
                    key={t.id}
                    className="hover:bg-[#F0F4FF]/50 transition-colors group"
                    data-testid={`tarefa-row-${t.id}`}
                  >
                    <td className="px-3 sm:px-5 py-3">
                      <div>
                        <p className={`text-sm font-semibold ${t.status === 'concluída' ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                          {t.titulo}
                        </p>
                        {t.descricao && (
                          <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[200px]">{t.descricao}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-3 sm:px-5 py-3 text-slate-500 text-sm whitespace-nowrap">
                      {t.prazo ? formatDate(t.prazo) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 sm:px-5 py-3">
                      <StatusBadge type="tarefa" value={t.status} />
                    </td>
                    <td className="px-3 sm:px-5 py-3 hidden sm:table-cell">
                      <StatusBadge type="prioridade" value={t.prioridade || 'média'} />
                    </td>
                    <td className="px-3 sm:px-5 py-3">
                      <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openEditTarefa(t)}
                          className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-[#0147a0] transition-colors"
                          data-testid={`edit-tarefa-${t.id}`}
                          title="Editar"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => deleteTarefa(t.id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                          data-testid={`delete-tarefa-${t.id}`}
                          title="Excluir"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL TAREFA */}
      {showTarefaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}>
          <div className="nexus-modal w-full max-w-md" data-testid="tarefa-form">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editTarefa ? 'Editar Tarefa' : 'Nova Tarefa'}
              </h3>
              <button onClick={closeTarefaModal} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="fin-label">Título *</label>
                <input type="text" value={tarefaForm.titulo}
                  onChange={e => setTarefaForm(p => ({ ...p, titulo: e.target.value }))}
                  className="fin-input" placeholder="Título da tarefa" data-testid="tarefa-titulo-input" />
              </div>
              <div>
                <label className="fin-label">Descrição</label>
                <input type="text" value={tarefaForm.descricao}
                  onChange={e => setTarefaForm(p => ({ ...p, descricao: e.target.value }))}
                  className="fin-input" placeholder="Descrição (opcional)" data-testid="tarefa-descricao-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="fin-label">Vencimento</label>
                  <input type="date" value={tarefaForm.prazo}
                    onChange={e => setTarefaForm(p => ({ ...p, prazo: e.target.value }))}
                    className="fin-input" data-testid="tarefa-prazo-input" />
                </div>
                <div>
                  <label className="fin-label">Prioridade</label>
                  <select value={tarefaForm.prioridade}
                    onChange={e => setTarefaForm(p => ({ ...p, prioridade: e.target.value }))}
                    className="fin-input" data-testid="tarefa-prioridade-select">
                    {PRIORIDADE_OPTS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="fin-label">Status</label>
                <select value={tarefaForm.status}
                  onChange={e => setTarefaForm(p => ({ ...p, status: e.target.value }))}
                  className="fin-input" data-testid="tarefa-status-select">
                  {STATUS_OPTS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3 px-5 pb-5">
              <button onClick={closeTarefaModal} className="fin-btn-secondary flex-1 justify-center" data-testid="cancel-tarefa-btn">
                <X size={14} /> Cancelar
              </button>
              <button onClick={saveTarefa} className="fin-btn-primary flex-1 justify-center" data-testid="save-tarefa-btn">
                <Check size={14} /> {editTarefa ? 'Atualizar' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL NOTA */}
      {showNotaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}>
          <div className="nexus-modal w-full max-w-md" data-testid="nota-form">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editNota ? 'Editar Nota' : 'Nova Nota'}
              </h3>
              <button onClick={closeNotaModal} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="fin-label">Título *</label>
                <input type="text" value={notaForm.titulo}
                  onChange={e => setNotaForm(p => ({ ...p, titulo: e.target.value }))}
                  className="fin-input" placeholder="Título da nota" data-testid="nota-titulo-input" />
              </div>
              <div>
                <label className="fin-label">Conteúdo *</label>
                <textarea value={notaForm.conteudo}
                  onChange={e => setNotaForm(p => ({ ...p, conteudo: e.target.value }))}
                  className="fin-input resize-none" rows={5}
                  placeholder="Escreva sua nota aqui..." data-testid="nota-conteudo-input" />
              </div>
            </div>
            <div className="flex gap-3 px-5 pb-5">
              <button onClick={closeNotaModal} className="fin-btn-secondary flex-1 justify-center" data-testid="cancel-nota-btn">
                <X size={14} /> Cancelar
              </button>
              <button onClick={saveNota} className="fin-btn-primary flex-1 justify-center" data-testid="save-nota-btn">
                <Check size={14} /> {editNota ? 'Atualizar' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
